<template>
    <div class="content">
      <div class="content-wrap">
        <h3>{{ t('add-role').toUpperCase() }}</h3>
        <div class="card-body">
          <Form
            ref="form"
            @submit="onSubmit"
            :validation-schema="schema"
            v-slot="{ errors, isSubmitting }"
            :keep-values="true"
          >
            <!-- Dane podstawowe -->
            <div class="column d-flex justify-content-center role-data">
              <div class="col-sm-6">
                <div class="form-group row">
                  <label class="col-sm-4 col-form-label">{{ t('name2') }}:</label>
                  <div class="col-sm-8">
                    <Field
                      name="name"
                      as="input"
                      type="text"
                      class="form-control"
                      :class="{ 'is-invalid': !!errors?.name }"
                    />
                    <div class="invalid-feedback">{{ errors.name }}</div>
                  </div>
                </div>
              </div>
  
              <div class="col-sm-6">
                <div class="form-group row">
                  <label class="col-sm-4 col-form-label">{{ t('description') }}:</label>
                  <div class="col-sm-8">
                    <Field
                      name="description"
                      as="textarea"
                      class="form-control"
                      :class="{ 'is-invalid': !!errors?.description }"
                    />
                    <div class="invalid-feedback">{{ errors.description }}</div>
                  </div>
                </div>
              </div>
            </div>
  
            <!-- Uprawnienia (kafelki jak w edycji) -->
            <div class="column d-flex justify-content-center role-data">
              <div class="form-group row w-100">
                <div class="col-sm-12">
                  <!-- ZAZNACZONE -->
                  <p class="mb-2 text-muted" v-if="selectedPermissionsDetails.length">Zaznaczone:</p>
                  <div class="roles-tiles mb-3" v-if="selectedPermissionsDetails.length">
                    <label
                      v-for="perm in selectedPermissionsDetails"
                      :key="'sel-'+perm.name"
                      class="role-tile selected"
                    >
                      <Field
                        name="permissions"
                        as="input"
                        type="checkbox"
                        class="sr-only"
                        :value="perm.name"
                        v-model="selectedPermissions"
                        :aria-checked="selectedPermissions.includes(perm.name)"
                        :aria-label="perm.name"
                      />
                      <div class="role-title">{{ perm.name }}</div>
                      <div class="role-desc" v-if="perm.description">{{ perm.description }}</div>
                    </label>
                  </div>
  
                  <!-- DOSTĘPNE -->
                  <p class="mb-2 text-muted">Dostępne:</p>
                  <div
                    class="roles-tiles"
                    :class="{ 'is-invalid': !!formErrors.permissions }"
                    role="group"
                    aria-label="permissions"
                  >
                    <label
                      v-for="perm in availablePermissions"
                      :key="'avail-'+perm.name"
                      class="role-tile"
                      :class="{ selected: selectedPermissions.includes(perm.name) }"
                    >
                      <Field
                        name="permissions"
                        as="input"
                        type="checkbox"
                        class="sr-only"
                        :value="perm.name"
                        v-model="selectedPermissions"
                        :aria-checked="selectedPermissions.includes(perm.name)"
                        :aria-label="perm.name"
                      />
                      <div class="role-title">{{ perm.name }}</div>
                      <div class="role-desc" v-if="perm.description">{{ perm.description }}</div>
                    </label>
                  </div>
  
                  <div class="invalid-feedback d-block">{{ formErrors.permissions }}</div>
                </div>
              </div>
            </div>
  
            <!-- Akcje / komunikaty -->
            <div class="form-group row d-flex flex-column align-items-center process">
              <div class="col-auto" v-if="processError">
                <div class="process-error form-control">{{ processError }}</div>
              </div>
              <div class="col-auto" v-if="processSuccess">
                <div class="process-success form-control">{{ processSuccess }}</div>
              </div>
              <div class="col-auto">
                <button type="button" @click="goBack" class="btn button-add-user">
                  {{ t('cancel') }}
                </button>
                <button class="btn button-save" :disabled="isSubmitting">
                  <span v-show="isSubmitting" class="spinner-border spinner-border-sm mr-1"></span>
                  {{ t('save').toUpperCase() }}
                </button>
              </div>
            </div>
          </Form>
        </div>
      </div>
    </div>
  </template>
  
  <script setup>
  import moment from "moment";
  import { Form, Field } from "vee-validate";
  import * as Yup from "yup";
  import { useI18n } from "vue-i18n";
  
  moment.locale("pl");
  const { t } = useI18n();
  
  const schema = Yup.object({
    name: Yup.string().required(t('name-required')),
    description: Yup.string(),
    permissions: Yup.array().of(Yup.string()).min(1, t('user-permissions-required')),
  });
  </script>
  
  <script>
  import RolesService from "@/services/Roles";
  
  export default {
    name: "AddRole",
    data() {
      return {
        rolesAvailable: [],          // lista „ról” zwracanych z API (zawierają tablice permissions)
        selectedPermissions: [],     // zaznaczone uprawnienia (nazwy, np. "ROLE_27")
        processError: "",
        processSuccess: "",
        formErrors: {},              // lokalny holder na errors.permissions, żeby pokazać pod siatką
      };
    },
    created() {
      // pobierz listę ról (źródło master listy uprawnień)
      RolesService.getRolesList()
        .then((data) => {
          this.rolesAvailable = Array.isArray(data) ? data : [];
        })
        .catch((e) => (this.processError = e?.message || String(e)));
    },
    methods: {
      goBack() {
        this.$emit("cancel-add-role");
      },
      // wyciągnij numer z nazwy (ROLE_10 -> 10) do sortowania
      getPermId(p) {
        if (p && typeof p.id === "number") return p.id;
        const m = (p?.name ?? "").match(/\d+/);
        const n = m ? parseInt(m[0], 10) : NaN;
        return Number.isFinite(n) ? n : Number.MAX_SAFE_INTEGER;
      },
      sortByPermId(a, b) {
        const da = this.getPermId(a);
        const db = this.getPermId(b);
        if (da !== db) return da - db;
        return (a?.name ?? "").localeCompare(b?.name ?? "");
      },
      onSubmit(values) {
        this.processSuccess = "";
        this.processError = "";
        this.formErrors = {};
  
        // jeśli Vee-Validate nie wstrzyknął permissions (np. user nic nie dotknął),
        // to weź z local state
        const perms = Array.from(new Set(values.permissions ?? this.selectedPermissions));
  
        if (!perms.length) {
          // pokaż błąd w tym samym miejscu co w edycji
          this.formErrors.permissions = this.$t?.('user-permissions-required') || 'Wybierz co najmniej jedno uprawnienie.';
          return;
        }
  
        const payload = {
          name: (values.name || "").trim(),
          description: (values.description || "").trim(),
          permissions: perms, // ["ROLE_10", "ROLE_11", ...]
        };
  
        RolesService.addRole(payload)
          .then(() => {
            this.processSuccess = "Utworzono rolę.";
            // reset formu + stanów
            this.selectedPermissions = [];
            this.$refs.form?.resetForm();
            this.$emit('role-added');
            this.$router.push('/role-group-management');
          })
          .catch((err) => {
            console.error(err);
            this.processError = err?.response?.data?.message || err?.message || "Błąd zapisu.";
          });
      },
    },
    computed: {
      // unikalna, posortowana master-lista uprawnień
      allPermissions() {
        const map = new Map();
        (this.rolesAvailable || []).forEach(r => {
          (r.permissions || []).forEach(p => {
            const obj = (typeof p === "string") ? { name: p, description: "" } : p;
            if (!map.has(obj.name)) map.set(obj.name, obj);
          });
        });
        return Array.from(map.values()).sort(this.sortByPermId);
      },
      selectedPermissionsDetails() {
        const set = new Set(this.selectedPermissions);
        return this.allPermissions.filter(p => set.has(p.name));
      },
      availablePermissions() {
        const set = new Set(this.selectedPermissions);
        return this.allPermissions.filter(p => !set.has(p.name));
      },
    },
  };
  </script>
  
<style scoped>

h3 { 
    padding: 15px 25px;
    background-color: white;
}

input[type="checkbox"] {
    margin-top: 13px;
}

.sr-only {
    position: absolute !important;
    width: 1px; height: 1px;
    padding: 0; margin: -1px;
    overflow: hidden; clip: rect(0,0,0,0);
    white-space: nowrap; border: 0;
}

.roles-tiles {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
    gap: 12px;
}

.role-tile {
    border: 1px solid #dcdcdc;
    border-radius: 12px;
    padding: 12px 14px;
    background: #fff;
    cursor: pointer;
    user-select: none;
    transition: border-color .15s ease, box-shadow .15s ease, background .15s ease;
}

.role-tile:hover {
    border-color: #bfbfbf;
    background: #fcfcfc;
}

.role-tile.selected {
    border-color: #0d6efd;
    box-shadow: 0 0 0 2px rgba(13,110,253,.15);
    background: #f6faff;
}

.role-title { font-weight: 600; line-height: 1.2; }
.role-desc { margin-top: 6px; font-size: 12px; color: #666; }
.roles-tiles.is-invalid { border: 1px dashed #dc3545; border-radius: 10px; padding: 8px; }
.col-form-label { text-align: right; }
.process-error { color: darkred; margin-bottom: 1rem; }
.process-success { color: #42b983; margin-bottom: 1rem; }
.content { width: 100%; min-height: 270px; margin-bottom: 20px; }
.card-body .column { padding: 20px 30px; }
.card-body .column > div { padding: 5px 20px; }
.role-data { border-bottom: 1px solid #b8b8b6; }
.invalid-feedback { font-size: 11px; }
.button-add-user { margin-top: 1rem; margin-bottom: 1rem; padding: .5rem 2rem; margin-right: .5rem; }
.button-save { margin-top: 1rem; margin-bottom: 1rem; padding: .5rem 2rem; border: 1px solid #007e1a; }
.button-save:hover {
  background-color: #005511 !important;
  border: 1px solid #005511 !important;
}
</style>
  
